import { transition, trigger, useAnimation } from '@angular/animations';
import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';

import { ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AttributeApiModel } from '@rocksoft/inroom-sdk';
import { pulseUpdate } from '../../../shared/animations/pulse.animation';
import { Attributes } from './attributes-store/attributes.state';

export interface FormAttributes {
  code: string;
  value: string;
  type: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'era-attributes-ui',
  templateUrl: './attributes.component.html',
  animations: [trigger('pulse', [transition('* => *', [useAnimation(pulseUpdate)])])],
  imports: [ReactiveFormsModule, FaIconComponent],
})
export class AttributesComponent {
  private readonly fb = inject(UntypedFormBuilder);

  readonly data = input.required<Attributes>();
  readonly save = output<Attributes>();

  editMode = false;

  public readonly attributesForm: UntypedFormGroup = this.fb.group({
    attributes: this.fb.array([]),
  });

  get attributes(): UntypedFormArray {
    return this.attributesForm.get('attributes') as UntypedFormArray;
  }

  constructor() {
    effect(() => {
      const data = this.data();

      this.attributes.clear();

      if (data) {
        data.attributes.forEach(() => {
          this.addAttribute();
        });

        const d: FormAttributes[] = data.attributes.map((x) => this.mapAttributeToForm(x));
        this.attributes.patchValue(d);
      }
    });
  }

  public addAttribute() {
    this.attributes.push(
      this.fb.group({
        code: [''],
        value: [''],
        type: [''],
      })
    );
  }

  public deleteAttribute(index: number) {
    this.attributes.removeAt(index);
  }

  public toggleEditMode() {
    if (this.editMode) {
      this.saveChanges();
    }

    this.editMode = !this.editMode;
  }

  private saveChanges() {
    const result: Attributes = {
      reservationId: this.data().reservationId,
      attributes: (this.attributes.value as FormAttributes[]).map((x) => this.mapAttributeFromForm(x)),
    };

    this.save.emit(result);
  }

  private mapAttributeFromForm(attribute: FormAttributes): AttributeApiModel {
    return {
      Code: attribute.code,
      Value: attribute.value,
      Type: attribute.type,
    };
  }

  private mapAttributeToForm(attribute: AttributeApiModel): FormAttributes {
    return {
      code: attribute.Code ?? '',
      value: attribute.Value ?? '',
      type: attribute.Type ?? '',
    };
  }
}
